import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  SimpleGrid, Button, Box, Text, VStack, HStack, Icon, Textarea, useToast,
  Input, FormControl, FormLabel, FormHelperText, Checkbox, Wrap, WrapItem,
} from '@chakra-ui/react';
import {
  FaCheck, FaSyringe, FaTimes, FaUserSlash, FaMapMarkerAlt,
  FaHeartBroken, FaLock, FaTools, FaPhoneSlash, FaArrowLeft,
  FaInbox, FaClipboardList, FaInfoCircle, FaVoicemail, FaPhoneAlt, FaPhone,
  FaSms, FaEnvelope, FaLanguage, FaCalendarCheck, FaCheckCircle, FaRedo,
  FaRoute, FaMapPin, FaBox, FaShippingFast,
} from 'react-icons/fa';
import { getAllOutcomes, getOutcomeConfig, getOutcomePhases, getOutcomePhaseLabel, getOutcomeFlags } from '../../utils/statusBadge';
import { logSubmissionOutcome, CONFIRMING_OUTCOMES } from '../../utils/logOutcome';
import { scheduleStateFromRow, buildSchedulePayload, emptyScheduleState } from '../../utils/schedule';

// Icon names from the outcome config -> react-icons components
const ICONS = {
  check: FaCheck,
  partial: FaSyringe,
  syringe: FaSyringe,
  'no-show': FaUserSlash,
  refused: FaTimes,
  map: FaMapMarkerAlt,
  unfit: FaHeartBroken,
  lock: FaLock,
  tools: FaTools,
  cancel: FaPhoneSlash,
  // intake
  inbox: FaInbox,
  review: FaClipboardList,
  info: FaInfoCircle,
  // contact
  voicemail: FaVoicemail,
  'no-answer': FaPhoneAlt,
  sms: FaSms,
  email: FaEnvelope,
  phone: FaPhone,
  language: FaLanguage,
  calendar: FaCalendarCheck,
  confirm: FaCheckCircle,
  reschedule: FaRedo,
  // visit
  route: FaRoute,
  pin: FaMapPin,
  // delivery
  drop: FaBox,
  ship: FaShippingFast,
};

/**
 * Bottom-drawer-style outcome picker used by phlebs at the moment of the visit.
 * Two phases: tile picker → optional note → save.
 *
 * Props:
 *   isOpen           — boolean
 *   onClose          — () => void
 *   submission       — the submissions row being acted on
 *   phlebotomistId   — passed through into outcome_logged_by
 *   onLogged         — (updatedFields) => void  callback after a successful save
 */
export default function OutcomeDrawer({ isOpen, onClose, submission, phlebotomistId, loggedByKind, loggedById, loggedByName, onLogged }) {
  const toast = useToast();
  const [selectedCode, setSelectedCode] = useState(null);
  const [note, setNote] = useState('');
  const [flags, setFlags] = useState([]);
  const [schedule, setSchedule] = useState(emptyScheduleState);
  const [saving, setSaving] = useState(false);
  // Synchronous guard — `saving` state can't block double-taps because setState is async.
  // The ref flips immediately, so a second click sees the in-flight state right away.
  const savingRef = useRef(false);

  // Reset internal state every time the drawer (re)opens. Pre-fill the
  // scheduled-time input from whatever the submission currently has so the
  // phleb edits an existing time rather than starting blank.
  useEffect(() => {
    if (isOpen) {
      setSelectedCode(null);
      setNote('');
      setFlags(Array.isArray(submission?.outcome_flags) ? submission.outcome_flags : []);
      setSchedule(scheduleStateFromRow(submission));
      setSaving(false);
      savingRef.current = false;
    }
  }, [isOpen, submission]);

  const outcomes = getAllOutcomes();
  const config = selectedCode ? getOutcomeConfig(selectedCode) : null;

  // Every disposition gets the detail step so the phleb can attach a note —
  // labs need to see the why behind each status change, not just the code.
  // Notes stay optional (unless cfg.needsNote forces one); the Save button
  // is always one click away.
  const showsDetailStep = (cfg) => !!cfg;

  const onTileClick = (code) => {
    const cfg = getOutcomeConfig(code);
    if (!cfg) return;
    setSelectedCode(code);
  };

  const save = async (code, noteValue, scheduleState, flagsValue) => {
    // Synchronous re-entry guard — a fast double-tap could otherwise fire two parallel
    // UPDATEs before the React setSaving state propagated to disable the tile.
    if (savingRef.current) return;
    if (!submission?.id) {
      toast({ title: 'No submission selected', status: 'error', duration: 3000 });
      return;
    }
    const cfg = getOutcomeConfig(code);
    if (!cfg) {
      toast({ title: 'Unknown outcome', status: 'error', duration: 3000 });
      return;
    }
    // Capture the submission id at the start of the save so we don't rely on the parent
    // still holding it in `outcomeSubmission` state — the modal might have been closed
    // or reopened for a different patient by the time this resolves.
    const targetId = submission.id;
    savingRef.current = true;
    setSaving(true);
    try {
      const { updateFields } = await logSubmissionOutcome({
        submission,
        code,
        note: noteValue,
        flags: flagsValue,
        schedule: buildSchedulePayload(scheduleState),
        phlebotomistId,
        // Optional explicit actor stamp — e.g. the admin detail page passes
        // adminUser.id + adminUser.name so the activity timeline shows
        // their actual name directly off the row.
        loggedByKind,
        loggedById,
        loggedByName,
      });

      toast({ title: `Logged: ${cfg.label}`, status: 'success', duration: 2500 });
      // Always pass the id along so the parent can patch the correct row even
      // if the user has since closed the drawer or opened it for someone else.
      if (onLogged) onLogged({ id: targetId, ...updateFields });
      onClose();
    } catch (err) {
      toast({ title: 'Could not save outcome', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const showDetailStep = showsDetailStep(config);
  // The scheduled-time field only makes sense while the phleb is in the
  // patient-contact phase. For visit/delivery outcomes the time is past tense
  // and editing it would be confusing.
  const showScheduledTimeField = !!(config && config.phase === 'contact');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="xl" mx={4} overflow="hidden">
        <ModalHeader bgGradient="linear(to-r, teal.500, blue.500)" color="white" py={2.5}>
          <HStack>
            {showDetailStep && (
              <Button size="xs" variant="ghost" color="white" leftIcon={<FaArrowLeft />} onClick={() => setSelectedCode(null)} _hover={{ bg: 'whiteAlpha.300' }}>
                Back
              </Button>
            )}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="800">Update Draw Status</Text>
              {submission && (
                <Text fontSize="2xs" opacity={0.9}>
                  {submission.patient_name || 'Patient'}
                  {submission.draw_code ? ` · #${submission.draw_code}` : ''}
                  {submission.lab_order_number ? ` · Order #${submission.lab_order_number}` : ''}
                </Text>
              )}
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" size="sm" />
        <ModalBody py={3}>
          {!showDetailStep ? (
            // ── Tile picker, grouped by phase. Compact: icon + label only;
            //    the full description shows on the detail step after picking. ──
            <VStack align="stretch" spacing={3}>
              {getOutcomePhases().map((phase) => {
                const phaseOutcomes = outcomes.filter((o) => o.phase === phase);
                if (phaseOutcomes.length === 0) return null;
                return (
                  <Box key={phase}>
                    <Text fontSize="2xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={1.5}>
                      {getOutcomePhaseLabel(phase)}
                    </Text>
                    <SimpleGrid columns={{ base: 3, md: 4 }} spacing={2}>
                      {phaseOutcomes.map(({ code, label, color, icon, followupRequired }) => {
                        const IconComp = ICONS[icon] || FaCheck;
                        return (
                          <Button
                            key={code}
                            onClick={() => onTileClick(code)}
                            isDisabled={saving}
                            h="auto"
                            py={2.5}
                            px={1.5}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                            borderRadius="lg"
                            variant="outline"
                            colorScheme={color}
                            whiteSpace="normal"
                            textAlign="center"
                            position="relative"
                          >
                            <Icon as={IconComp} boxSize={4} />
                            <Text fontWeight="600" fontSize="2xs" lineHeight="1.1" noOfLines={2}>{label}</Text>
                            {followupRequired && (
                              <Box position="absolute" top={1} right={1} w={1.5} h={1.5} borderRadius="full" bg="orange.400" title="Follow-up required" />
                            )}
                          </Button>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                );
              })}
            </VStack>
          ) : (
            // ── Detail step: note, plus a scheduled-time picker when the
            //    phleb is in the patient-contact phase ────────────────────
            <VStack align="stretch" spacing={4}>
              <Box bg={`${config.color}.50`} border="1px" borderColor={`${config.color}.200`} p={3} borderRadius="lg">
                <HStack>
                  <Icon as={ICONS[config.icon] || FaCheck} color={`${config.color}.500`} />
                  <Box>
                    <Text fontWeight="700" fontSize="sm" color={`${config.color}.700`}>{config.label}</Text>
                    <Text fontSize="xs" color="gray.600">{config.description}</Text>
                  </Box>
                </HStack>
              </Box>

              {config.supportsFlags && (
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>
                    Additional status <Text as="span" fontSize="xs" color="gray.400" fontWeight="400">(optional — applies to either result)</Text>
                  </FormLabel>
                  <Wrap spacing={2}>
                    {getOutcomeFlags().map((f) => {
                      const checked = flags.includes(f.key);
                      return (
                        <WrapItem key={f.key}>
                          <Box
                            as="button"
                            type="button"
                            onClick={() => setFlags((prev) => prev.includes(f.key) ? prev.filter((k) => k !== f.key) : [...prev, f.key])}
                            px={2.5}
                            py={1.5}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={checked ? `${f.color}.400` : 'gray.200'}
                            bg={checked ? `${f.color}.50` : 'white'}
                          >
                            <HStack spacing={1.5}>
                              <Checkbox isChecked={checked} pointerEvents="none" colorScheme={f.color} size="sm" />
                              <Text fontSize="xs" fontWeight="600" color={checked ? `${f.color}.700` : 'gray.600'}>{f.label}</Text>
                            </HStack>
                          </Box>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                  <FormHelperText fontSize="xs">Tag anything notable about the draw. Add specifics in the note below.</FormHelperText>
                </FormControl>
              )}

              {showScheduledTimeField && (
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Patient availability</FormLabel>
                  <HStack spacing={2} mb={2}>
                    {[['exact', 'Specific time'], ['window', 'Window'], ['all_day', 'All day']].map(([val, label]) => (
                      <Button
                        key={val}
                        size="xs"
                        variant={schedule.type === val ? 'solid' : 'outline'}
                        colorScheme={schedule.type === val ? config.color : 'gray'}
                        onClick={() => setSchedule((s) => ({ ...s, type: val }))}
                      >
                        {label}
                      </Button>
                    ))}
                  </HStack>

                  {schedule.type === 'exact' && (
                    <Input
                      type="datetime-local"
                      size="sm"
                      value={schedule.exact}
                      onChange={(e) => setSchedule((s) => ({ ...s, exact: e.target.value }))}
                    />
                  )}

                  {schedule.type === 'window' && (
                    <VStack align="stretch" spacing={2}>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="2xs" color="gray.500" mb={0.5}>From date</Text>
                          <Input
                            type="date" size="sm" value={schedule.startDate}
                            onChange={(e) => setSchedule((s) => ({ ...s, startDate: e.target.value }))}
                          />
                        </Box>
                        <Box>
                          <Text fontSize="2xs" color="gray.500" mb={0.5}>To date</Text>
                          <Input
                            type="date" size="sm" value={schedule.endDate} min={schedule.startDate || undefined}
                            onChange={(e) => setSchedule((s) => ({ ...s, endDate: e.target.value }))}
                          />
                        </Box>
                      </SimpleGrid>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="2xs" color="gray.500" mb={0.5}>From time (optional)</Text>
                          <Input
                            type="time" size="sm" value={schedule.startTime}
                            onChange={(e) => setSchedule((s) => ({ ...s, startTime: e.target.value }))}
                          />
                        </Box>
                        <Box>
                          <Text fontSize="2xs" color="gray.500" mb={0.5}>To time (optional)</Text>
                          <Input
                            type="time" size="sm" value={schedule.endTime}
                            onChange={(e) => setSchedule((s) => ({ ...s, endTime: e.target.value }))}
                          />
                        </Box>
                      </SimpleGrid>
                    </VStack>
                  )}

                  {schedule.type === 'all_day' && (
                    <Box>
                      <Text fontSize="2xs" color="gray.500" mb={0.5}>Date</Text>
                      <Input
                        type="date" size="sm" value={schedule.startDate}
                        onChange={(e) => setSchedule((s) => ({ ...s, startDate: e.target.value }))}
                      />
                    </Box>
                  )}

                  <FormHelperText fontSize="xs">
                    {CONFIRMING_OUTCOMES.has(selectedCode)
                      ? 'Confirming will assign this draw to you.'
                      : schedule.type === 'exact'
                      ? 'Optional — set or update the planned visit time.'
                      : schedule.type === 'window'
                      ? 'Patient is reachable in this range. Leave times blank for anytime.'
                      : 'Patient is available all day on this date.'}
                  </FormHelperText>
                </FormControl>
              )}

              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  Notes {!config.needsNote && <Text as="span" fontSize="xs" color="gray.400" fontWeight="400">(optional)</Text>}
                </FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {selectedCode === 'address_wrong'
                    ? 'What is the correct address (or what was wrong)?'
                    : selectedCode === 'partial_draw'
                    ? 'Which tubes were drawn vs. failed?'
                    : selectedCode === 'language_barrier'
                    ? 'Which language does the patient speak?'
                    : selectedCode === 'missing_info'
                    ? 'What is missing (insurance card, script, ICD codes, etc.)?'
                    : config.phase === 'contact'
                    ? 'What did the patient say? Any callback time, preferred language, etc.'
                    : 'Add any details that admin should see.'}
                </Text>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Type a quick note..."
                  rows={3}
                  resize="vertical"
                  maxLength={500}
                  autoFocus
                />
                <Text fontSize="2xs" color="gray.400" textAlign="right">{note.length}/500</Text>
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" mr={2} onClick={onClose} isDisabled={saving}>
            Cancel
          </Button>
          {showDetailStep && (
            <Button
              colorScheme={config.color}
              onClick={() => save(selectedCode, note, schedule, config.supportsFlags ? flags : [])}
              isLoading={saving}
              loadingText="Saving"
              isDisabled={config.needsNote && !note.trim()}
            >
              Save Outcome
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
